import { useState, useEffect, useCallback } from 'react';
import { DataTable } from '../../components/shared/DataTable';
import { CertificateModal } from '../../components/content/CertificateModal';
import { ConfirmDeleteModal } from '../../components/content/ConfirmDeleteModal';
import { searchCertificates, deleteCertificate } from '../../services/content/certificateService';
import { useRefresh } from '../../contexts/RefreshContext';
import { useAssociationLookup } from '../../hooks/useAssociationLookup';
import type { Certificate } from '../../types/content/certificate';
import type { Column } from '../../components/shared/DataTable';

function RowMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[100px]">
          <button onClick={() => { setOpen(false); onEdit(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Edit</button>
          <button onClick={() => { setOpen(false); onDelete(); }} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">Delete</button>
        </div>
      )}
    </div>
  );
}

export default function CertificatesTab() {
  const { resolveAssociationName } = useAssociationLookup();
  const [data, setData] = useState<Certificate[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalCert, setModalCert] = useState<Certificate | undefined>(undefined);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const columns: Column<Certificate>[] = [
    { key: 'name', label: 'Name' },
    {
      key: 'association',
      label: 'Association',
      render: (item) => {
        if (!item.association) return '—';
        const name = resolveAssociationName(item.association, item.associationId);
        return name ? `${item.association}: ${name}` : item.association;
      },
    },
    {
      key: 'description',
      label: 'Description',
      render: (item) => item.description ? (
        <span className="truncate max-w-[200px] block">{item.description}</span>
      ) : '—',
    },
  ];

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const results = await searchCertificates({}, { page, pageSize, orderBy: 'updated', order: 'desc' });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { fetch(); }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/content/certificates', fetch);
    return () => unregisterRefreshHandler('/content/certificates');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  async function handleDelete(id: string) {
    await deleteCertificate(id);
    setPendingDeleteId(null);
    fetch();
  }

  return (
    <>
      <DataTable
        title="Certificates"
        columns={columns}
        data={data}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        onRefresh={fetch}
        onAdd={() => { setModalCert(undefined); setShowModal(true); }}
        alignTop
        rowMenu={(item) => (
          <RowMenu
            onEdit={() => { setModalCert(item); setShowModal(true); }}
            onDelete={() => setPendingDeleteId(item.id)}
          />
        )}
      />
      {pendingDeleteId && (
        <ConfirmDeleteModal title="Delete Certificate" message="Are you sure you want to delete this certificate?" onCancel={() => setPendingDeleteId(null)} onConfirm={() => handleDelete(pendingDeleteId)} />
      )}
      {showModal && (
        <CertificateModal
          certificate={modalCert}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); fetch(); }}
        />
      )}
    </>
  );
}
